// src/components/modals/QuickAddPetModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { petAPI } from '../../services/api';
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { useFlatpickrUserType } from "../../hooks/useFlatpickrUserType";
import './QuickAddPetModal.css';

const QuickAddPetModal = ({ isOpen, onClose, onPetAdded, userTypeColor }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  
  // Apply user type color to Flatpickr calendars globally
  useFlatpickrUserType();

  const [formData, setFormData] = useState({
    petName: '',
    species_id: '',
    breed: '',
    gender: '',
    age: '', // This will be used as birth_date
  });

  const [speciesList, setSpeciesList] = useState([]);
  const [breedList, setBreedList] = useState([]);
  const [errors, setErrors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const scrollLockRef = useRef(0);

  // Fetch Species
  useEffect(() => {
    if (isOpen) {
      petAPI.getPetsBySpecies()
        .then((res) => {
          setSpeciesList(Array.isArray(res.data) ? res.data : res.data.data || []);
        })
        .catch((err) => console.error('Error fetching species:', err));
    } else {
      // Reset form
      setFormData({
        petName: '',
        species_id: '',
        breed: '',
        gender: '',
        age: '',
      });
      setErrors([]);
    }
  }, [isOpen]);

  // Fetch Breeds
  useEffect(() => {
    if (formData.species_id) {
      petAPI.getBreedBySpecies(formData.species_id)
        .then((res) => {
          setBreedList(Array.isArray(res.data) ? res.data : res.data.data || []);
        })
        .catch((err) => console.error('Error fetching breeds:', err));
    } else {
      setBreedList([]);
    }
  }, [formData.species_id]);

  // Handle scroll lock
  useEffect(() => {
    if (!isOpen) return;
    scrollLockRef.current = window.scrollY || window.pageYOffset || 0;
    const originalStyle = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
    };
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollLockRef.current}px`;
    document.body.style.width = '100%';

    return () => {
      document.body.style.overflow = originalStyle.overflow;
      document.body.style.position = originalStyle.position;
      document.body.style.top = originalStyle.top;
      document.body.style.width = originalStyle.width;
      window.scrollTo(0, scrollLockRef.current || 0);
    };
  }, [isOpen]);

  // Initialize Flatpickr for birth date
  useEffect(() => {
    if (!isOpen) return;

    const birthDatePicker = flatpickr("#quick-pet-birth-date", {
      dateFormat: "Y-m-d",
      maxDate: "today",
      disableMobile: "true", // Force custom picker on mobile
      onChange: (selectedDates, dateStr) => {
        setFormData(prev => ({ ...prev, age: dateStr }));
      }
    });

    return () => {
      if (birthDatePicker) birthDatePicker.destroy();
    };
  }, [isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors([]);
    
    // Validation
    const validationErrors = [];
    if (!formData.petName.trim()) validationErrors.push(t('addPet.petNameRequired') || 'Pet name is required');
    if (!formData.species_id) validationErrors.push(t('addPet.speciesRequired') || 'Species is required');
    if (!formData.breed) validationErrors.push(t('addPet.breedRequired') || 'Breed is required');
    if (!formData.gender) validationErrors.push(t('addPet.genderRequired') || 'Gender is required');

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsLoading(true);

    try {
      const apiPayload = {
        name: formData.petName.trim(),
        species_id: formData.species_id,
        breed_id: formData.breed,
        gender: formData.gender,
        adopted_rescued: "adopted",
        color: null,
        provider_name: null,
        provider_number: null,
        policy_number: "",
        birth_date: formData.age || null,
        adoption_date: null,
        microchip_id: "",
        smart_collar_id: "",
        weight: 0,
        size_category: "medium",
        is_spayed_neutered: 0,
        bio: "No bio provided",
        special_notes: "",
        allergies: "",
        last_known_location: "Central Park, New York",
        is_location_tracking_enabled: 1,
        is_public_profile: 1,
        allow_social_interactions: 1,
        profile_image: null,
      };

      const response = await petAPI.createPet(apiPayload);
      const newPet = response.data?.data || response.data;
      onPetAdded(newPet);
    } catch (error) {
      console.error('Error saving pet from quick modal:', error);
      if (error.response?.data?.errors) {
        const apiErrors = error.response.data.errors;
        setErrors(Object.values(apiErrors).flat());
      } else if (error.response?.data?.message) {
        setErrors([error.response.data.message]);
      } else {
        setErrors([t('addPet.genericError') || 'An error occurred while creating the pet.']);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="quick-add-pet-overlay" onClick={onClose}>
      <div className="quick-add-pet-modal" onClick={e => e.stopPropagation()} dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="quick-add-pet-header">
          <h2>{t('addPet.pageTitle') || 'Add a Pet'}</h2>
          <button className="quick-add-pet-close" onClick={onClose} disabled={isLoading}>&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="quick-add-pet-form">
          {errors.length > 0 && (
            <div className="quick-add-pet-errors">
              {errors.map((err, i) => (
                <p key={i}>{err}</p>
              ))}
            </div>
          )}

          <div className="quick-add-pet-field">
            <label>{t('addPet.petName') || 'Pet Name'} *</label>
            <input
              type="text"
              name="petName"
              value={formData.petName}
              onChange={handleInputChange}
              disabled={isLoading}
              className="quick-add-pet-input"
            />
          </div>

          <div className="quick-add-pet-field">
            <label>{t('addPet.species') || 'Species'} *</label>
            <select
              name="species_id"
              value={formData.species_id}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, species_id: e.target.value, breed: '' }));
              }}
              disabled={isLoading}
              className="quick-add-pet-input"
            >
              <option value="" disabled>{t('addPet.speciesPlaceholder') || 'Select Species'}</option>
              {speciesList.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          <div className="quick-add-pet-field">
            <label>{t('addPet.breed') || 'Breed'} *</label>
            <select
              name="breed"
              value={formData.breed}
              onChange={handleInputChange}
              disabled={isLoading || !formData.species_id}
              className="quick-add-pet-input"
            >
              <option value="" disabled>{t('addPet.breedPlaceholder') || 'Select Breed'}</option>
              {breedList.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>

          <div className="quick-add-pet-field">
            <label>{t('addPet.gender') || 'Gender'} *</label>
            <div className="quick-add-pet-radio-group">
              <label>
                <input
                  type="radio"
                  name="gender"
                  value="female"
                  checked={formData.gender === 'female'}
                  onChange={handleInputChange}
                  disabled={isLoading}
                />
                {t('addPet.female') || 'Female'}
              </label>
              <label>
                <input
                  type="radio"
                  name="gender"
                  value="male"
                  checked={formData.gender === 'male'}
                  onChange={handleInputChange}
                  disabled={isLoading}
                />
                {t('addPet.male') || 'Male'}
              </label>
            </div>
          </div>

          <div className="quick-add-pet-field">
            <label>{t('addPet.birthDateOptional') || 'Birth Date'}</label>
            <input
              type="text"
              id="quick-pet-birth-date"
              name="age"
              value={formData.age}
              placeholder={t('addPet.birthDatePlaceholder') || "YYYY-MM-DD"}
              readOnly={true} // Controlled by flatpickr
              disabled={isLoading}
              className="quick-add-pet-input"
            />
          </div>

          <div className="quick-add-pet-actions">
            <button type="button" onClick={onClose} disabled={isLoading} className="quick-add-pet-cancel">
              {t('common.cancel') || 'Cancel'}
            </button>
            <button 
              type="submit" 
              disabled={isLoading} 
              className="quick-add-pet-submit"
              style={{ backgroundColor: userTypeColor || '#8548A8' }}
            >
              {isLoading ? (t('common.saving') || 'Saving...') : (t('common.save') || 'Save')}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default QuickAddPetModal;
